import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { Search, Send } from "lucide-react";
import { searchCandidates, inviteCandidate } from "@/lib/api/lxp-recruiter.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/recruiter")({
  head: () => ({ meta: [{ title: "Recruiter Dashboard | SA LexPractiq" }] }),
  component: RecruiterPage,
});

function RecruiterPage() {
  const searchFn = useServerFn(searchCandidates);
  const inviteFn = useServerFn(inviteCandidate);
  const [area, setArea] = useState("");
  const [minScore, setMinScore] = useState(70);
  const [items, setItems] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [subject, setSubject] = useState("Interview invitation");
  const [message, setMessage] = useState("");

  async function run() {
    setBusy(true);
    try {
      const r: any = await searchFn({ data: { practiceArea: area || undefined, minScore } });
      setItems(r.items);
    } catch (e: any) { toast.error(e?.message || "Search failed"); }
    finally { setBusy(false); }
  }

  useEffect(() => { run(); }, []);

  async function invite() {
    if (!selected) return;
    try {
      await inviteFn({ data: { candidateId: selected.user_id, subject, message } });
      toast.success("Invitation sent");
      setSelected(null);
    } catch (e: any) { toast.error(e?.message); }
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <h1 style={{ margin: "0 0 0.5rem" }}>Recruiter Dashboard</h1>
        <p className="lxp-muted">Search candidates by demonstrated skill and invite them to interview.</p>

        <div className="lxp-card" style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "end" }}>
          <label style={{ flex: 1, minWidth: 180 }}>
            <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Practice area</div>
            <input className="lxp-input" placeholder="e.g. Labour Law" value={area} onChange={(e) => setArea(e.target.value)} />
          </label>
          <label style={{ flex: 1, minWidth: 140 }}>
            <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Min score %</div>
            <input className="lxp-input" type="number" min={0} max={100} value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} />
          </label>
          <button className="lxp-btn" onClick={run} disabled={busy}>
            <Search size={14} /> Search
          </button>
        </div>

        <div style={{ display: "grid", gap: "0.75rem", marginTop: "1.5rem" }}>
          {items.map((r) => (
            <div key={r.id} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
              <div>
                <span className="lxp-badge">{r.simulation?.practice_area}</span>
                <h3 style={{ margin: "0.4rem 0 0.2rem", fontSize: "0.95rem" }}>{r.simulation?.title}</h3>
                <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                  Candidate {r.user_id.slice(0, 8)}… · Score {r.final_score}% · Completed {new Date(r.completed_at).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.4rem" }}>
                <a className="lxp-btn-ghost" href={`/lexpractiq/portfolio/${r.user_id}`} target="_blank" rel="noreferrer">View portfolio</a>
                <button className="lxp-btn" onClick={() => setSelected(r)}><Send size={14} /> Invite</button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="lxp-muted">No candidates match your filters yet.</p>}
        </div>

        {selected && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "grid", placeItems: "center", padding: "1rem", zIndex: 50 }} onClick={() => setSelected(null)}>
            <div className="lxp-card" style={{ maxWidth: 520, width: "100%", background: "var(--lxp-surface)" }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: "0 0 1rem" }}>Send invitation</h3>
              <input className="lxp-input" style={{ marginBottom: "0.75rem" }} value={subject} onChange={(e) => setSubject(e.target.value)} />
              <textarea className="lxp-textarea" rows={6} placeholder="Message to the candidate…" value={message} onChange={(e) => setMessage(e.target.value)} />
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", justifyContent: "flex-end" }}>
                <button className="lxp-btn-ghost" onClick={() => setSelected(null)}>Cancel</button>
                <button className="lxp-btn" onClick={invite}>Send</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LxpLayout>
  );
}
